import { GCoinsApi, GCOINS_HISTORY_LIMIT } from '@/api';
import {
  GCoinsAdminActionForm,
  GCoinsHistoryFilters,
  GCoinGeeksHistory,
  GeeksWithdrawal,
  WaitingWithdrawalFilters,
  WaitingWithdrawalForm,
  WaitingRequest,
  WaitingRequestForm,
  WaitingRequestFilters,
  WaitingWithdrawalAcceptForm,
  WaitingRequestRejectForm,
  ExportWithdrawalsFilters,
  GCoinsToImport,
 } from '@/models/admin-panel';
import {
  CustomAwardForm,
  GCoinGeekHistory,
  GeeksBalance,
  GeeksBalanceFilters,
  RewardableActivity,
  WithdrawForm,
} from '@/models/gcoins';
import { Module } from 'vuex';
import { Actions } from './actions';
import { RootState } from './root.store';
import { Mutations } from './mutations';
import { isWithdrawalsCollapseVisible, initializeCollapsedList, initializeNotCollapsedList } from '@/shared';

function initializeWithdrawals(data: GeeksWithdrawal[]) {
  data.forEach((e: GeeksWithdrawal, index: number) => {
    if (isWithdrawalsCollapseVisible(data[index])) {
      data[index] = { ...e, collapsed: true };
    }
  });
}

export interface GCoinsState {
  activities: RewardableActivity[];
  history: GCoinGeekHistory[];
  historyWithGeek: GCoinGeeksHistory[];
  waitingRequests: WaitingRequest[];
  withdrawalsToExport: GeeksWithdrawal[];
  waitingWithdrawals: GeeksWithdrawal[];
  balanceEntries: GeeksBalance[];
  allGeeksBalance: number;
  gCoinsToImport: GCoinsToImport[];
  pagination: {
    offset: number,
    totalCount: number,
  };
}

const gCoinsApi = new GCoinsApi();

export const GCoinsModule: Module<GCoinsState, RootState> = {
  state: {
    activities: [],
    history: [],
    historyWithGeek: [],
    waitingRequests: [],
    withdrawalsToExport: [],
    waitingWithdrawals: [],
    allGeeksBalance: 0,
    balanceEntries: [],
    gCoinsToImport: [],
    pagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.gCoins.setActivities](state, activities: RewardableActivity[]) {
      state.activities = activities;
    },
    [Mutations.gCoins.setHistory](state, history: GCoinGeekHistory[]) {
      state.history = history;
    },
    [Mutations.gCoins.setHistoryWithGeek](state, historyWithGeek: GCoinGeeksHistory[]) {
      state.historyWithGeek = historyWithGeek;
    },
    [Mutations.gCoins.setWaitingRequests](state, waitingRequests: WaitingRequest[]) {
      state.waitingRequests = waitingRequests;
    },
    [Mutations.gCoins.setWithdrawalsToExport](state, withdrawalsToExport: GeeksWithdrawal[]) {
      state.withdrawalsToExport = withdrawalsToExport;
    },
    [Mutations.gCoins.setWaitingWithdrawals](state, waitingWithdrawals: GeeksWithdrawal[]) {
      state.waitingWithdrawals = waitingWithdrawals;
    },
    [Mutations.gCoins.setElementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.gCoins.setAllGeeksBalance](state, allGeeksBalance: number) {
      state.allGeeksBalance = allGeeksBalance;
    },
    [Mutations.gCoins.addHistoryElements](state, history: GCoinGeekHistory[]) {
      state.history.push(...history);
    },
    [Mutations.gCoins.addHistoryWithGeekElements](state, historyWithGeek: GCoinGeeksHistory[]) {
      state.historyWithGeek.push(...historyWithGeek);
    },
    [Mutations.gCoins.setBalanceEntries](state, balanceEntries: GeeksBalance[]) {
      state.balanceEntries = balanceEntries;
    },
    [Mutations.gCoins.addBalanceEntries](state, balanceEntries: GeeksBalance[]) {
      state.balanceEntries.push(...balanceEntries);
    },
    [Mutations.gCoins.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.gCoins.incrementPagination](state) {
      state.pagination.offset += GCOINS_HISTORY_LIMIT;
    },
    [Mutations.gCoins.setGCoinsFromFile](state, gCoinsToImport: GCoinsToImport[]) {
      state.gCoinsToImport = gCoinsToImport;
    },
    [Mutations.gCoins.resetGCoinsToImport](state) {
      state.gCoinsToImport = [];
    },
  },

  actions: {
    async [Actions.gCoins.addGCoins](_, formData: GCoinsAdminActionForm): Promise<void> {
      await gCoinsApi.addGCoins(formData);
    },
    async [Actions.gCoins.fetchAwards]({ commit }): Promise<void> {
      const activities = await gCoinsApi.fetchAwards();
      commit(Mutations.gCoins.setActivities, activities);
    },
    async [Actions.gCoins.fetchGCoinsHistory]({ commit }, filters: GCoinsHistoryFilters): Promise<void> {
      commit(Mutations.gCoins.resetPagination);
      const historyWithGeek = await gCoinsApi.fetchGCoinsHistory(filters);
      initializeNotCollapsedList<GCoinGeeksHistory>(historyWithGeek.data);
      commit(Mutations.gCoins.setHistoryWithGeek, historyWithGeek.data);
      commit(Mutations.gCoins.setElementsCount, historyWithGeek.totalCount);
    },
    async [Actions.gCoins.fetchMoreGCoinsHistory]({ commit, state }, filters: GCoinsHistoryFilters): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const historyWithGeek = await gCoinsApi.fetchGCoinsHistory(filters, state.pagination.offset);
      initializeNotCollapsedList<GCoinGeeksHistory>(historyWithGeek.data);
      commit(Mutations.gCoins.addHistoryWithGeekElements, historyWithGeek.data);
      commit(Mutations.gCoins.setElementsCount, historyWithGeek.totalCount);
    },
    async [Actions.gCoins.fetchWithdrawalsToExport]({ commit }, filters: ExportWithdrawalsFilters): Promise<void> {
      commit(Mutations.gCoins.resetPagination);
      const withdrawalsToExport = await gCoinsApi.fetchWithdrawalsToExport(filters);
      initializeWithdrawals(withdrawalsToExport.data);
      commit(Mutations.gCoins.setWithdrawalsToExport, withdrawalsToExport.data);
      commit(Mutations.gCoins.setElementsCount, withdrawalsToExport.totalCount);
    },
    async [Actions.gCoins.fetchMoreWithdrawalsToExport](
      { commit, state },
      filters: ExportWithdrawalsFilters,
    ): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const withdrawalsToExport = await gCoinsApi.fetchWithdrawalsToExport(filters, state.pagination.offset);
      initializeWithdrawals(withdrawalsToExport.data);
      commit(Mutations.gCoins.setWithdrawalsToExport, withdrawalsToExport.data);
      commit(Mutations.gCoins.setElementsCount, withdrawalsToExport.totalCount);
    },
    async [Actions.gCoins.fetchWaitingWithdrawals]({ commit }, filters: WaitingWithdrawalFilters): Promise<void> {
      commit(Mutations.gCoins.resetPagination);
      const waitingWithdrawals = await gCoinsApi.fetchWaitingWithdrawals(filters);
      initializeWithdrawals(waitingWithdrawals.data);
      commit(Mutations.gCoins.setWaitingWithdrawals, waitingWithdrawals.data);
      commit(Mutations.gCoins.setElementsCount, waitingWithdrawals.totalCount);
    },
    async [Actions.gCoins.fetchMoreWaitingWithdrawals](
      { commit, state },
      filters: WaitingWithdrawalFilters,
    ): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const waitingWithdrawals = await gCoinsApi.fetchWaitingWithdrawals(filters, state.pagination.offset);
      initializeWithdrawals(waitingWithdrawals.data);
      commit(Mutations.gCoins.setWaitingWithdrawals, waitingWithdrawals.data);
      commit(Mutations.gCoins.setElementsCount, waitingWithdrawals.totalCount);
    },
    async [Actions.gCoins.fetchWaitingRequests]({ commit }, filters: WaitingRequestFilters): Promise<void> {
      commit(Mutations.gCoins.resetPagination);
      const waitingRequests = await gCoinsApi.fetchWaitingRequests(filters);
      initializeCollapsedList<WaitingRequest>(waitingRequests.data);
      commit(Mutations.gCoins.setWaitingRequests, waitingRequests.data);
      commit(Mutations.gCoins.setElementsCount, waitingRequests.totalCount);
    },
    async [Actions.gCoins.fetchMoreWaitingRequests](
      { commit, state },
      filters: WaitingRequestFilters,
    ): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const waitingRequests = await gCoinsApi.fetchWaitingRequests(filters, state.pagination.offset);
      initializeCollapsedList<WaitingRequest>(waitingRequests.data);
      commit(Mutations.gCoins.setWaitingRequests, waitingRequests.data);
      commit(Mutations.gCoins.setElementsCount, waitingRequests.totalCount);
    },
    async [Actions.gCoins.fetchGeekHistory]({ commit }): Promise<void> {
      commit(Mutations.gCoins.resetPagination);
      const history = await gCoinsApi.fetchGeekHistory();
      initializeNotCollapsedList<GCoinGeekHistory>(history.data);
      commit(Mutations.gCoins.setHistory, history.data);
      commit(Mutations.gCoins.setElementsCount, history.totalCount);
    },
    async [Actions.gCoins.fetchAllGeeksBalance]({ commit }): Promise<void> {
      const allGeeksBalance = await gCoinsApi.fetchAllGeeksBalance();
      commit(Mutations.gCoins.setAllGeeksBalance, allGeeksBalance);
    },
    async [Actions.gCoins.fetchGeeksBalanceEntries](
      { commit },
      filters: GeeksBalanceFilters,
    ): Promise<{data: GeeksBalance[], totalCount: number}> {
      commit(Mutations.gCoins.resetPagination);
      const balanceEntries = await gCoinsApi.fetchGeeksBalanceEntries(filters);
      commit(Mutations.gCoins.setBalanceEntries, balanceEntries.data);
      commit(Mutations.gCoins.setElementsCount, balanceEntries.totalCount);
      return Promise.resolve(balanceEntries);
    },
    async [Actions.gCoins.fetchMoreBalanceEntries]({ commit, state }, filters: GeeksBalanceFilters): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const balanceEntries = await gCoinsApi.fetchGeeksBalanceEntries(filters, state.pagination.offset);
      commit(Mutations.gCoins.addBalanceEntries, balanceEntries.data);
      commit(Mutations.gCoins.setElementsCount, balanceEntries.totalCount);
    },
    async [Actions.gCoins.fetchMoreHistory]({ commit, state }): Promise<void> {
      commit(Mutations.gCoins.incrementPagination);
      const history = await gCoinsApi.fetchGeekHistory(state.pagination.offset);
      initializeNotCollapsedList<GCoinGeekHistory>(history.data);
      commit(Mutations.gCoins.addHistoryElements, history.data);
      commit(Mutations.gCoins.setElementsCount, history.totalCount);
    },
    async [Actions.gCoins.withdraw](_, formData: WithdrawForm): Promise<void> {
      await gCoinsApi.withdraw(formData);
    },
    async [Actions.gCoins.exchangeGCoins](_, formData: GCoinsAdminActionForm): Promise<void> {
      await gCoinsApi.exchangeGCoins(formData);
    },
    async [Actions.gCoins.requestCustomAward](_, formData: CustomAwardForm): Promise<void> {
      await gCoinsApi.requestCustomAward(formData);
    },
    async [Actions.gCoins.fetchBalances](): Promise<Blob> {
      return await gCoinsApi.fetchBalances();
    },
    async [Actions.gCoins.fetchWithdrawals](_, filters: ExportWithdrawalsFilters): Promise<Blob> {
      return await gCoinsApi.fetchWithdrawals(filters);
    },
    async [Actions.gCoins.acceptWithdrawal](_, formData: WaitingWithdrawalForm): Promise<void> {
      await gCoinsApi.acceptWithdrawal(formData);
    },
    async [Actions.gCoins.rejectWithdrawal](_, formData: WaitingWithdrawalForm): Promise<void> {
      await gCoinsApi.rejectWithdrawal(formData);
    },
    async [Actions.gCoins.acceptRequest](_, formData: WaitingWithdrawalAcceptForm): Promise<void> {
      await gCoinsApi.acceptRequest(formData);
    },
    async [Actions.gCoins.rejectRequest](_, formData: WaitingRequestRejectForm): Promise<void> {
      await gCoinsApi.rejectRequest(formData);
    },
    async [Actions.gCoins.orderRequest](_, formData: WaitingRequestForm): Promise<void> {
      await gCoinsApi.orderRequest(formData);
    },
    async [Actions.gCoins.confirmRequest](_, formData: WaitingRequestForm): Promise<void> {
      await gCoinsApi.confirmRequest(formData);
    },
    async [Actions.gCoins.handleGCoinsToImportData]({ commit }, fileData: string): Promise<void> {
      const gCoinsToImport = await gCoinsApi.handleGCoinsToImportData(fileData);
      commit(Mutations.gCoins.setGCoinsFromFile, gCoinsToImport);
    },
    async [Actions.gCoins.resetGCoinsToImportData]({ commit }): Promise<void> {
      commit(Mutations.gCoins.resetGCoinsToImport);
    },
    async [Actions.gCoins.sendGCoinsToImportData](_, gCoinsToImport: GCoinsToImport[]): Promise<void> {
      await gCoinsApi.sendGCoinsToImportData(gCoinsToImport);
    },
  },
};
